import React, { useState, useEffect } from "react";
import { Grid } from "@mui/material";
import PropTypes from "prop-types";
import { useDispatch } from "react-redux";
import CustomModal from "../../../../../components/CustomModal/CustomModal";
import CustomTextField from "../../../../../components/CustomTextField";
import CustomButton from "../../../../../components/CustomButton";
import { updateClinicUserById } from "../../../../../redux/reducers/clinicSlice";

const getInitials = (name) => {
  if (!name) return "P";
  const parts = name.trim().split(" ").filter(Boolean);
  if (parts.length === 1) return parts[0].substring(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const EditClientProfileModal = ({ open, onClose, patientDetails, onSave }) => {
  const dispatch = useDispatch();
  const [usrValues, setUsrValues] = useState({});
  const [usrErrs, setUsrErrs] = useState({});

  useEffect(() => {
    if (open && patientDetails) {
      setUsrValues({
        userName: patientDetails.userName || patientDetails.name || "",
        userMobile: patientDetails.userMobile || patientDetails.phone || patientDetails.mobile || "",
        email: patientDetails.email || patientDetails.userEmail || "",
        secondaryMobile: patientDetails.secondaryMobile || "",
        address: patientDetails.address || "",
        userId: patientDetails.userId || patientDetails.id || null
      });
      setUsrErrs({});
    }
  }, [open, patientDetails]);

  const handleValueChange = (value, name) => {
    const valObj = { ...usrValues, [name]: value };
    setUsrValues(valObj);
    const errObj = {
      ...usrErrs,
      [name]:
        value?.length > 0
          ? ""
          : name === "email" || name === "secondaryMobile" || name === "address"
            ? ""
            : "This field is required"
    };
    setUsrErrs(errObj);
  };

  const handleClose = () => {
    setUsrValues({});
    setUsrErrs({});
    onClose();
  };

  const updateUser = () => {
    const userId = usrValues?.userId || patientDetails?.userId || patientDetails?.id;
    if (
      !usrValues?.userName ||
      usrValues?.userName?.trim()?.length === 0 ||
      !usrValues?.userMobile ||
      usrValues?.userMobile?.trim()?.length === 0
    ) {
      let errs = { ...usrErrs };
      if (!usrValues?.userName || usrValues?.userName?.trim()?.length === 0) {
        errs = { ...errs, userName: "This field is required" };
      }
      if (!usrValues?.userMobile || usrValues?.userMobile?.trim()?.length === 0) {
        errs = { ...errs, userMobile: "This field is required" };
      }
      setUsrErrs(errs);
      return;
    }

    const updatedDetails = {
      ...patientDetails,
      name: usrValues.userName,
      userName: usrValues.userName,
      phone: usrValues.userMobile,
      userMobile: usrValues.userMobile,
      mobile: usrValues.userMobile,
      email: usrValues.email,
      secondaryMobile: usrValues.secondaryMobile,
      address: usrValues.address,
      avatarInitials: getInitials(usrValues.userName)
    };

    if (userId) {
      const form = new FormData();
      form.append("userName", usrValues?.userName);
      form.append("userMobile", usrValues?.userMobile);
      if (usrValues?.email?.length > 0) {
        form.append("email", usrValues?.email ?? "");
      }
      form.append("address", usrValues?.address ?? "");
      form.append("secondaryMobile", usrValues?.secondaryMobile ?? "");

      dispatch(updateClinicUserById({ userId, form })).then((res) => {
        if (res?.payload && res?.payload?.status !== 400) {
          if (onSave) {
            onSave(updatedDetails);
          }
          handleClose();
        }
      });
    } else {
      if (onSave) {
        onSave(updatedDetails);
      }
      handleClose();
    }
  };

  return (
    <CustomModal
      open={open}
      onClose={handleClose}
      header="Update"
      rightModal
      modalWidth={30}
    >
      <div className="scroll-80vh-new ph20 mt10">
        <Grid container spacing={2}>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="mt10">
              <CustomTextField
                label="Parent Name"
                name="userName"
                fullWidth
                handleChange={(e) =>
                  handleValueChange(e?.target?.value, "userName")
                }
                value={usrValues?.userName}
                error={!!usrErrs.userName}
                helperText={usrErrs.userName}
                required={true}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                label="Contact Number"
                name="userMobile"
                fullWidth
                handleChange={(e) =>
                  handleValueChange(e?.target?.value, "userMobile")
                }
                value={usrValues?.userMobile}
                error={!!usrErrs.userMobile}
                helperText={usrErrs.userMobile}
              />
            </div>
          </Grid>

          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                label="Email"
                name="email"
                fullWidth
                handleChange={(e) =>
                  handleValueChange(e?.target?.value, "email")
                }
                value={usrValues?.email}
                error={!!usrErrs.email}
                helperText={usrErrs.email}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                label="Secondary Phone Number"
                name="secondaryMobile"
                fullWidth
                handleChange={(e) =>
                  handleValueChange(e?.target?.value, "secondaryMobile")
                }
                value={usrValues?.secondaryMobile}
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div>
              <CustomTextField
                label="Address"
                name="address"
                fullWidth
                handleChange={(e) =>
                  handleValueChange(e?.target?.value, "address")
                }
                value={usrValues?.address}
                labelTop
                multiline
              />
            </div>
          </Grid>
          <Grid item xs={12} sm={12} md={12} lg={12} xl={12}>
            <div className="flex1-end">
              <div>
                <CustomButton text="Update" onClick={updateUser} smallBtn />
              </div>
            </div>
          </Grid>
        </Grid>
      </div>
    </CustomModal>
  );
};

EditClientProfileModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  patientDetails: PropTypes.object,
  onSave: PropTypes.func
};

export default EditClientProfileModal;
